import { useCallback, useState } from 'react';
import { TAnimalTypeRetractionNoticeUI } from 'data/types/animal.types';
import AnimalTypeRetractionNoticeModal from '.';

/**
 * PP-10057. Wraps the retraction dialog so a call site only has to hand over what the mutation
 * returned and say what to do once the tenant has read it.
 *
 * `onAcknowledged` runs after the tenant closes the dialog rather than straight after the mutation,
 * because the flows that trigger this normally navigate or close a form on success — doing that
 * first would unmount the dialog before it is read.
 */
export const useAnimalTypeRetractionNotices = () => {
  const [notices, setNotices] = useState<TAnimalTypeRetractionNoticeUI[]>([]);
  const [onAcknowledged, setOnAcknowledged] = useState<(() => void) | null>(
    null,
  );

  /** @returns true when a dialog was opened, so the caller can defer its own success handling. */
  const showRetractionNotices = useCallback(
    (
      retractionNotices: TAnimalTypeRetractionNoticeUI[] | undefined,
      afterAcknowledged?: () => void,
    ): boolean => {
      if (!retractionNotices?.length) {
        return false;
      }
      setNotices(retractionNotices);
      setOnAcknowledged(() => afterAcknowledged ?? null);
      return true;
    },
    [],
  );

  const handleClose = useCallback(() => {
    setNotices([]);
    if (onAcknowledged) {
      onAcknowledged();
      setOnAcknowledged(null);
    }
  }, [onAcknowledged]);

  const retractionNoticeModal = notices.length ? (
    <AnimalTypeRetractionNoticeModal notices={notices} onClose={handleClose} />
  ) : null;

  return { showRetractionNotices, retractionNoticeModal };
};
